import { zodResolver } from "@hookform/resolvers/zod";
import { Button, TextField } from "@mui/material";
import {
  CaretDownIcon,
  AirplaneTakeoffIcon,
  AirplaneLandingIcon,
} from "@phosphor-icons/react";
import dayjs from "dayjs";
import { get } from "lodash-es";
import { type ReactNode, useMemo, useState } from "react";
import {
  Controller,
  FormProvider,
  useForm,
  useWatch,
  type FieldPath,
} from "react-hook-form";
import { useTranslations } from "use-intl";

import { DrawerTopBar } from "@/components/Drawer/DrawerTopBar";
import { SwipeableDrawer } from "@/components/Drawer/SwipeableDrawer";
import { ResetButton } from "@/components/ResetButton/ResetButton";
import SwapValuesButton from "@/components/SwapValuesButton/SwapValuesButton";
import {
  AirportType,
  CabinClass,
  TripType,
} from "@/features/flights/types/types";
import { initialFlightsValues } from "@/features/flights/utils/initialFlightsValues";
import { usePreviousFlightSearchData } from "@/hooks/usePreviousSearchData";
import { useSnackbarRHFErrors } from "@/hooks/useSnackbarRHFErrors";
import {
  rudderAnalytics,
  FlightSearchTrackingEvents,
  type SearchSource,
} from "@/lib/rudderstack";
import {
  type TFlightFormValues,
  type TFlightFormFieldValues,
} from "@/types/types";
import { getDateString } from "@/utils/dates";
import { getCityName } from "@/utils/locations";

import AirportSearch from "./AirportSearch";
import { DatesGuard } from "./DatesGuard";
import DatesSelection from "./DatesSelection";
import TravellersSelection from "./TravellersSelection";
import TripTypeRadioGroup from "./TripTypeRadioGroup";
import { getValidationSchema } from "./validation";

enum DrawerComponent {
  AirportSearch = "AirportSearch",
  DatesSelection = "DatesSelection",
  PassengersSelection = "PassengersSelection",
}

const cabinClassLabels = {
  [CabinClass.ANY]: "anyClass",
  [CabinClass.ECONOMY]: "economy",
  [CabinClass.BUSINESS]: "business",
  [CabinClass.PREMIUM_ECONOMY]: "premiumEconomy",
  [CabinClass.FIRST_CLASS]: "firstClass",
};

type FlightFormFieldPath = FieldPath<TFlightFormValues>;

type DrawerParams =
  | {
      component: DrawerComponent.AirportSearch;
      airportType: AirportType;
    }
  | {
      component: DrawerComponent.DatesSelection;
    }
  | {
      component: DrawerComponent.PassengersSelection;
    };

type Props = {
  submitButtonContent: ReactNode;
  initialValues?: TFlightFormValues | null;
  showErrorsInline?: boolean;
  onSubmit: (values: TFlightFormValues) => void;
  analyticsSource: SearchSource;
};

export const FlightCalculator = ({
  submitButtonContent,
  initialValues,
  showErrorsInline,
  onSubmit,
  analyticsSource,
}: Props) => {
  const [drawerParams, setDrawerParams] = useState<DrawerParams | null>(null);
  const { previousSearchData } = usePreviousFlightSearchData();
  const t = useTranslations();

  const schema = useMemo(() => getValidationSchema(t), [t]);

  const methods = useForm<TFlightFormFieldValues, unknown, TFlightFormValues>({
    defaultValues: initialValues || previousSearchData,
    resolver: zodResolver(schema),
    mode: "onBlur",
    reValidateMode: "onChange",
  });

  const { reset, handleSubmit, control, getValues, setValue, formState } =
    methods;

  useSnackbarRHFErrors<TFlightFormFieldValues>({
    disabled: showErrorsInline,
    errors: formState.errors,
    touched: formState.touchedFields,
    submitCount: formState.submitCount,
  });

  const [
    adult,
    child,
    infantWithoutSeat,
    cabinClass,
    tripType,
    from,
    to,
    dates,
  ] = useWatch({
    control,
    name: [
      "adult",
      "child",
      "infantWithoutSeat",
      "cabinClass",
      "tripType",
      "from",
      "to",
      "dates",
    ],
  });

  const { departure: datesDeparture, return: datesReturn } = dates;
  const passengerCount = adult + child + infantWithoutSeat;

  const changeAirportValues = () => {
    setValue("from", to);
    setValue("to", from);
  };

  const resetField = (
    e: React.MouseEvent<HTMLButtonElement>,
    type: FlightFormFieldPath,
  ) => {
    e.stopPropagation();
    setValue(type, get(initialFlightsValues, type));
  };

  const departureValue = dayjs(datesDeparture).format("ddd, D MMM");

  const returnValue = datesReturn
    ? dayjs(datesReturn).format("ddd, D MMM")
    : "_";

  return (
    <FormProvider {...methods}>
      <form
        className="w-full"
        noValidate
        acceptCharset="ISO-8859-1"
        onSubmit={handleSubmit((data) => onSubmit(data))}
        aria-label={t("flights.search.flightSearchForm")}
      >
        <Controller
          control={control}
          name="tripType"
          render={({ field }) => (
            <TripTypeRadioGroup
              {...field}
              onChange={(e, value) => {
                rudderAnalytics.track(
                  FlightSearchTrackingEvents.USER_CHANGED_TRIP_TYPE,
                  {
                    source: analyticsSource,
                    type: value as TripType,
                  },
                );

                field.onChange(e);
              }}
            />
          )}
        />

        <div className="mb-4 flex flex-col gap-2">
          <div className="relative flex flex-col gap-[inherit]">
            <TextField
              fullWidth
              onClick={() => {
                rudderAnalytics.track(
                  FlightSearchTrackingEvents.USER_CLICKED_FLIGHT_ORIGIN_INPUT,
                  { source: analyticsSource },
                );
                setDrawerParams({
                  component: DrawerComponent.AirportSearch,
                  airportType: AirportType.FROM,
                });
              }}
              slotProps={{
                input: {
                  endAdornment: from && (
                    <>
                      <ResetButton
                        aria-label={t("flights.search.resetDeparture")}
                        onClick={(e) => {
                          resetField(e, "from");
                        }}
                      />
                      {from.type === "AIRPORT" && (
                        <span className="absolute end-10 text-sm text-text-placeholder">
                          {from.code}
                        </span>
                      )}
                    </>
                  ),
                },
              }}
              sx={{
                input: {
                  paddingInlineEnd: "70px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  userSelect: "none",
                },
              }}
              value={getCityName(from)}
              error={showErrorsInline && !!formState.errors.from}
              helperText={showErrorsInline && formState.errors.from?.message}
              label={
                <div className="flex items-center gap-2">
                  <AirplaneTakeoffIcon size={24} />
                  <span>{t("flights.search.from")}</span>
                </div>
              }
            />

            <SwapValuesButton
              onClick={changeAirportValues}
              className="absolute end-5 top-1/2 z-10 -translate-y-1/2"
            />
            <TextField
              fullWidth
              value={getCityName(to)}
              error={showErrorsInline && !!formState.errors.to?.name}
              helperText={
                showErrorsInline && formState.errors.to?.name?.message
              }
              onClick={() => {
                rudderAnalytics.track(
                  FlightSearchTrackingEvents.USER_CLICKED_FLIGHT_DESTINATION_INPUT,
                  { source: analyticsSource },
                );
                setDrawerParams({
                  component: DrawerComponent.AirportSearch,
                  airportType: AirportType.TO,
                });
              }}
              slotProps={{
                input: {
                  endAdornment: to && (
                    <>
                      <ResetButton
                        aria-label={t("flights.search.resetArrival")}
                        onClick={(e) => {
                          resetField(e, "to");
                        }}
                      />
                      {to.type === "AIRPORT" && (
                        <span className="absolute end-10 text-sm text-text-placeholder">
                          {to.code}
                        </span>
                      )}
                    </>
                  ),
                },
              }}
              label={
                <div className="flex items-center gap-2">
                  <AirplaneLandingIcon size={24} />
                  <span>{t("flights.search.to")}</span>
                </div>
              }
              sx={{
                input: {
                  paddingInlineEnd: "70px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  userSelect: "none",
                },
              }}
            />
          </div>
          <TextField
            name="dates"
            onClick={() => {
              rudderAnalytics.track(
                FlightSearchTrackingEvents.USER_CLICKED_FLIGHT_CALENDAR_INPUT,
                { source: analyticsSource },
              );
              setDrawerParams({
                component: DrawerComponent.DatesSelection,
              });
            }}
            fullWidth
            label={t("flights.search.departureDate")}
            slotProps={{
              input: {
                endAdornment:
                  datesDeparture &&
                  !dayjs(datesDeparture).isSame(
                    initialFlightsValues.dates.departure,
                    "day",
                  ) ? (
                    <ResetButton
                      aria-label={t("flights.search.resetDates")}
                      onClick={(e) => {
                        resetField(e, "dates.departure");
                      }}
                    />
                  ) : null,
              },
            }}
            value={
              tripType === TripType.ROUND_TRIP
                ? `${departureValue} - ${returnValue}`
                : departureValue
            }
          />
          <TextField
            name="passengersNo"
            fullWidth
            id="passengersNo"
            label={t("flights.search.travellers")}
            className="w-full p-0"
            slotProps={{
              input: {
                endAdornment: (
                  <CaretDownIcon
                    className="shrink-0"
                    color="black"
                    size={20}
                    weight="bold"
                  />
                ),
              },
            }}
            value={`${passengerCount > 1 ? t("persons") : t("person")} ${passengerCount}, ${t(
              `flights.cabinClass.${cabinClassLabels[cabinClass]}`,
            )}`}
            onClick={() => {
              rudderAnalytics.track(
                FlightSearchTrackingEvents.USER_CLICKED_FLIGHT_TRAVELLERS_INPUT,
                {
                  source: analyticsSource,
                },
              );
              setDrawerParams({
                component: DrawerComponent.PassengersSelection,
              });
            }}
          />
        </div>

        <Button fullWidth variant="contained" type="submit">
          {submitButtonContent}
        </Button>
      </form>

      <DatesGuard />

      {/* we're not using open prop and must mount fresh instance of the component
        because otherwise initial opening animation breaks scroll when mobile keyboard is present */}
      {drawerParams ? (
        <SwipeableDrawer
          hasFullHeight={!!drawerParams}
          onClose={() => setDrawerParams(null)}
          open
          disableRestoreFocus
          heightOffset={24}
        >
          {drawerParams.component === DrawerComponent.AirportSearch && (
            <>
              <DrawerTopBar title={t("flights.search.flightRoute")} />
              <AirportSearch
                values={{ from, to }}
                onChange={setValue}
                onDrawerClose={() => setDrawerParams(null)}
                initialFocusOn={drawerParams.airportType}
                analyticsSource={analyticsSource}
              />
            </>
          )}
          {drawerParams.component === DrawerComponent.DatesSelection && (
            <DatesSelection
              initialValues={getValues()}
              analyticsSource={analyticsSource}
              onConfirm={(values) => {
                rudderAnalytics.track(
                  FlightSearchTrackingEvents.USER_CHANGED_CALENDAR_DATE,
                  {
                    type: values.tripType,
                    dateFrom: getDateString(values.dates.departure),
                    ...(values.tripType === TripType.ROUND_TRIP
                      ? {
                          dateTo: getDateString(values.dates.return!),
                        }
                      : {}),
                    source: analyticsSource,
                  },
                );
                reset(values);
                setDrawerParams(null);
              }}
            />
          )}
          {drawerParams.component === DrawerComponent.PassengersSelection && (
            <>
              <DrawerTopBar title={t("flights.search.travellersCabinClass")} />
              <TravellersSelection
                initialValues={getValues()}
                onConfirm={(values) => {
                  rudderAnalytics.track(
                    FlightSearchTrackingEvents.USER_CHANGED_TRAVELLERS_AND_CLASS,
                    {
                      travellers: {
                        adults: values.adult,
                        children: values.child,
                        infants: values.infantWithoutSeat,
                      },
                      class: values.cabinClass,
                      source: analyticsSource,
                    },
                  );
                  reset(values);
                  setDrawerParams(null);
                }}
              />
            </>
          )}
        </SwipeableDrawer>
      ) : null}
    </FormProvider>
  );
};
